import { useCallback, useMemo, useRef, useState } from "react";
import { Flex, Input as AntInput, Typography, InputRef, Button } from "antd";
import { Input, Tag, Popconfirm } from "@libertify/ui";
import { useThemeData } from "../hooks/useThemeData";
import {
  UpdateThemeRequest,
  useUpdateTheme,
  useDeleteTheme,
} from "../hooks/useThemeApi";
import { useTranslate } from "hooks/useTranslate/";
import { PanelSection } from "../../storyboardEditor/components/StoryboardMenuTabs/PanelSection";

export const ThemeInfoTab = () => {
  const { selectedTheme, organizationId, setSelectedTheme } = useThemeData();
  const t = useTranslate();

  const { mutateAsync: deleteThemeMutate, isPending: isDeleting } =
    useDeleteTheme(organizationId);
  const { mutateAsync } = useUpdateTheme(organizationId);
  const updateTheme = useCallback(
    async (data: UpdateThemeRequest) => {
      if (!selectedTheme?.id) return;
      await mutateAsync({ themeId: selectedTheme.id, data });
      const updated = { ...selectedTheme };
      if (data.name != null) updated.name = data.name;
      if (data.description != null) updated.description = data.description;
      if (data.tags != null) updated.tags = data.tags;
      setSelectedTheme(updated);
    },
    [mutateAsync, selectedTheme, setSelectedTheme]
  );

  const [name, setName] = useState(selectedTheme?.name ?? "");
  const [description, setDescription] = useState(
    selectedTheme?.description ?? ""
  );
  const [tagKeyInput, setTagKeyInput] = useState("");
  const [tagValueInput, setTagValueInput] = useState("");
  const tagKeyRef = useRef<InputRef>(null);

  // Keep local state in sync when switching themes
  const [trackedThemeId, setTrackedThemeId] = useState(selectedTheme?.id);
  if (selectedTheme && selectedTheme.id !== trackedThemeId) {
    setTrackedThemeId(selectedTheme.id);
    setName(selectedTheme.name);
    setDescription(selectedTheme.description);
  }

  const tags = useMemo(() => selectedTheme?.tags ?? [], [selectedTheme?.tags]);

  const handleSaveName = useCallback(() => {
    if (name === selectedTheme?.name) return;
    void updateTheme({ name });
  }, [selectedTheme?.name, name, updateTheme]);

  const handleSaveDescription = useCallback(() => {
    if (description === selectedTheme?.description) return;
    void updateTheme({ description });
  }, [selectedTheme?.description, description, updateTheme]);

  const handleAddTag = useCallback(() => {
    if (!tagKeyInput.trim() || !tagValueInput.trim()) return;

    const newTags = [...tags];
    const existing = newTags.find((tag) => tag.tag_key === tagKeyInput.trim());
    if (existing) {
      existing.tag_values = [
        ...new Set([...existing.tag_values, tagValueInput.trim()]),
      ];
    } else {
      newTags.push({
        tag_key: tagKeyInput.trim(),
        tag_values: [tagValueInput.trim()],
      });
    }
    void updateTheme({ tags: newTags });
    setTagKeyInput("");
    setTagValueInput("");
    tagKeyRef.current?.focus();
  }, [tags, tagKeyInput, tagValueInput, updateTheme]);

  const handleRemoveTag = useCallback(
    (tagKey: string, tagValue: string) => {
      const newTags = tags
        .map((tag) =>
          tag.tag_key === tagKey
            ? {
                ...tag,
                tag_values: tag.tag_values.filter((v) => v !== tagValue),
              }
            : tag
        )
        .filter((tag) => tag.tag_values.length > 0);

      void updateTheme({ tags: newTags });
    },
    [tags, updateTheme]
  );

  const handleDelete = useCallback(async () => {
    if (!selectedTheme?.id) return;
    await deleteThemeMutate(selectedTheme.id);
    setSelectedTheme(null);
  }, [deleteThemeMutate, selectedTheme, setSelectedTheme]);

  if (!selectedTheme) return null;

  return (
    <>
      <PanelSection>
        <Flex vertical gap={4}>
          <Typography.Text type="secondary">
            {t("media.themeEditor.themeInfo.name")}
          </Typography.Text>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleSaveName}
            onPressEnter={handleSaveName}
          />
        </Flex>
        <Flex vertical gap={4}>
          <Typography.Text type="secondary">
            {t("media.themeEditor.themeInfo.description")}
          </Typography.Text>
          <AntInput.TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleSaveDescription}
            rows={3}
          />
        </Flex>
      </PanelSection>

      <PanelSection title={t("media.themeEditor.themeInfo.tags")}>
        <Typography.Text type="secondary">
          {t("media.themeEditor.themeInfo.tagsDescription")}
        </Typography.Text>
        <Flex gap={8}>
          <Input
            ref={tagKeyRef}
            placeholder={t("media.themeEditor.createModal.tagKey")}
            value={tagKeyInput}
            onChange={(e) => setTagKeyInput(e.target.value)}
            style={{ flex: 1 }}
          />
          <Input
            placeholder={t("media.themeEditor.createModal.tagValue")}
            value={tagValueInput}
            onChange={(e) => setTagValueInput(e.target.value)}
            onPressEnter={handleAddTag}
            style={{ flex: 1 }}
          />
          <Button
            onClick={handleAddTag}
            disabled={!tagKeyInput.trim() || !tagValueInput.trim()}
            style={{ alignSelf: "stretch", height: "auto" }}
          >
            {t("buttons.add")}
          </Button>
        </Flex>
        <Flex wrap gap={4}>
          {tags.length > 0 ? (
            tags.flatMap((tag) =>
              tag.tag_values.map((value) => (
                <Tag
                  key={`${tag.tag_key}-${value}`}
                  onClose={() => handleRemoveTag(tag.tag_key, value)}
                  closable
                >
                  {tag.tag_key}: {value}
                </Tag>
              ))
            )
          ) : (
            <Typography.Text type="secondary">
              {t("media.themeEditor.themeInfo.noTags")}
            </Typography.Text>
          )}
        </Flex>
      </PanelSection>

      <PanelSection>
        <Popconfirm
          title={t("media.themeEditor.themeInfo.deleteConfirm")}
          onConfirm={() => void handleDelete()}
          confirmText={t("buttons.delete")}
          danger
          cancelText={t("buttons.cancel")}
        >
          <Button danger block loading={isDeleting}>
            {t("media.themeEditor.themeInfo.delete")}
          </Button>
        </Popconfirm>
      </PanelSection>
    </>
  );
};
